import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AccountConnectionStatus, IAccount, IAutoReplySettings, ISignature, MailboxType } from '../../../models/account.models';
import { InboxConfirmationActionType } from '../../../models/inbox.models';
import { SignatureService } from '../../../services/signature.service';
import { SharedModule } from 'src/app/shared/shared.module';
import { UiModule } from 'src/app/ui/ui.module';
import { AppState } from 'src/app/core/app.state';
import { AuthService } from 'src/app/features/auth/authentication/services/auth.service';
import { InboxConfirmationDialogComponent, InboxConfirmationSkipType } from '../../inbox-confirmation-dialog/inbox-confirmation-dialog.component';
import {
  InboxDisconnectDialogComponent,
  InboxDisconnectChoice,
} from '../inbox-disconnect-dialog/inbox-disconnect-dialog.component';
import { selectAccounts, selectUpdateLoading } from '../../../store/smart-inbox.selectors';
import * as smartInboxActions from '../../../store/smart-inbox.actions';

// ─────────────────────────────────────────────────────────────────────────────
// AccountSettingsDialogComponent
// ─────────────────────────────────────────────────────────────────────────────
// The single account modal, opened from the Smart Inbox sidebar gear and (once the
// permissions table moves here) from Settings > Smart Inbox > Connections.
//
//   • Details   – owner, display name, connection status + stats, account type
//   • Signature – multi-signature management with HTML editor & defaults
//   • Auto Reply – commented out until it has real settings behind it
//
// Connection status used to be its own tab. It is the thing users open this dialog
// to check, so it now sits inside Details and the tab is gone.
//
// Only the owner sees tabs: everyone else can act on the connection but has no
// signatures or auto-reply to manage, so a one-item tab bar would be noise.
// ─────────────────────────────────────────────────────────────────────────────

/** Tab positions. MUST stay in step with the `tabs` array below (the dialog opens on an index). */
export enum AccountSettingsTab {
  Details = 0,
  Signature = 1,
  /** Currently not rendered — see the commented entry in `tabs`. */
  AutoReply = 2,
}

export interface AccountSettingsDialogData {
  account: IAccount;
  /** Defaults to Details. */
  initialTab?: AccountSettingsTab;
  /**
   * Owner's display name when the caller already knows it. This dialog cannot
   * resolve a name for a mailbox the signed-in user does not own, so it falls back
   * to a generic label.
   */
  ownerName?: string;
}

@Component({
  selector: 'app-account-settings-dialog',
  standalone: true,
  templateUrl: './account-settings-dialog.component.html',
  styleUrls: ['./account-settings-dialog.component.scss'],
  imports: [CommonModule, UiModule, SharedModule, MatProgressSpinnerModule],
})
export class AccountSettingsDialogComponent implements OnInit, OnDestroy {

  // ── Input data ───────────────────────────────────────────────────────────
  account: IAccount;

  // ── Shared dialog options ────────────────────────────────────────────────
  dialogOptions = {
    hideHeader: true,
    hideActions: true,
    hideConfirmButton: true,
    // 900px was far wider than this content needs; the shell's own padding is
    // neutralised in the SCSS so the sections below own all the spacing.
    size: 'medium' as const,
  };

  // ── Details tab state ────────────────────────────────────────────────────
  isEditingName = false;
  editedName = '';
  selectedAccountType: MailboxType = 'personal';
  ownerName = '';
  loadingState = {
    name: false, type: false, refresh: false, disconnect: false,
    delete: false, reconnect: false,
  };

  // ── Signature tab state ────────────────────────────────────────────────────
  /** id = 0 is the local placeholder for a default signature not yet persisted */
  static readonly DEFAULT_SIGNATURE_PLACEHOLDER_ID = 0;
  signatures: ISignature[] = [];
  selectedSignatureId: number | null = null;
  signaturesLoading = false;
  signatureSaving = false;
  signatureEditMode: 'html' | 'upload' = 'html';
  renamingSignatureId: number | null = null;
  editedSignatureName = '';
  defaultNewId: number | null = null;
  defaultReplyForwardId: number | null = null;

  // ── Auto Reply tab state (dummy) ─────────────────────────────────────────
  autoReplySettings: IAutoReplySettings = { enabled: false };

  // ── Tab navigation ──────────────────────────────────────────────────────
  /** Set from `data.initialTab` in the constructor. Order matches AccountSettingsTab. */
  activeTabIndex: number = AccountSettingsTab.Details;
  readonly tabs = [
    { label: 'smart_inbox.inbox_view.account_settings_dialog.tab_details' },
    { label: 'smart_inbox.inbox_view.account_settings_dialog.tab_signature' },
    // Auto Reply is a single dummy toggle with nothing behind it yet — uncomment
    // this and the tab block in the template to bring it back.
    // { label: 'smart_inbox.inbox_view.account_settings_dialog.tab_auto_reply' },
  ];
  readonly AccountSettingsTab = AccountSettingsTab;

  /** Whether the signed-in user owns this mailbox. Drives the tab bar. */
  isOwner = false;
  /** Something was changed, so the opener should reload whatever list it shows. */
  private mutated = false;

  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<AccountSettingsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AccountSettingsDialogData,
    private matDialog: MatDialog,
    private actions$: Actions,
    private store: Store<AppState>,
    private router: Router,
    private authService: AuthService,
    private signatureService: SignatureService,
    private sanitizer: DomSanitizer,
  ) {
    this.account = data.account;
    this.activeTabIndex = data.initialTab ?? AccountSettingsTab.Details;
  }

  /** No live provider connection. */
  get isDisconnected(): boolean {
    return this.account.connection_status === AccountConnectionStatus.DISCONNECTED;
  }

  /** Live and syncing. Anything else — pending, error — is neither, and says so. */
  get isConnected(): boolean {
    return this.account.connection_status === AccountConnectionStatus.CONNECTED;
  }

  /** Tabs exist only for the owner; see the class comment. */
  get showTabs(): boolean {
    return this.isOwner;
  }

  /**
   * Account type and re-connect are owner-only: re-connect runs the owner's own
   * hosted-auth flow, and the type is the owner's call. Non-owners see them
   * read-only rather than getting a 403.
   */
  get canChangeType(): boolean {
    return this.isOwner;
  }

  get canReconnect(): boolean {
    return this.isOwner;
  }

  /** `gmail` -> `Gmail`. Falls back to the raw value rather than hiding it. */
  get providerLabel(): string {
    const map: Record<string, string> = { gmail: 'Gmail', o365: 'Outlook', exchange: 'Exchange' };
    const provider = this.account.provider ?? '';
    return map[provider] ?? provider;
  }

  /**
   * Asset name for the provider logo, the same one the settings connections table
   * shows. Empty when there is no logo for that provider (only gmail.svg and
   * outlook.svg exist), so the template can fall back instead of 404ing.
   */
  get providerIcon(): string {
    const map: Record<string, string> = { gmail: 'gmail', o365: 'outlook' };
    return map[this.account.provider ?? ''] ?? '';
  }

  /** Colour bucket for the status dot and badge, shared with the sidebar's vocabulary. */
  get connectionTone(): 'ok' | 'pending' | 'error' {
    switch (this.account.connection_status) {
      case AccountConnectionStatus.CONNECTED: return 'ok';
      case AccountConnectionStatus.PENDING: return 'pending';
      default: return 'error';
    }
  }

  get ownerInitials(): string {
    const parts = (this.ownerName || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    return (parts.length === 1 ? parts[0].slice(0, 2) : parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.selectedAccountType = this.account.mailbox_type;
    this.editedName = this.account.name;
    this.isOwner = this.account.mailbox_owner_id === this.authService.getCurrentUser()?.id;
    this.resolveOwnerName();
    // Signatures live behind owner-scoped routes and only the owner has a tab for
    // them, so a non-owner must not ask.
    if (this.isOwner) {
      this.loadSignatures();
    }

    this.store.select(selectUpdateLoading)
      .pipe(takeUntil(this.destroy$))
      .subscribe((loading) => {
        this.loadingState = {
          name: loading.accountName,
          type: loading.accountType,
          refresh: loading.accountRefresh,
          disconnect: loading.accountDisconnect,
          delete: loading.accountDelete,
          reconnect: loading.accountReconnect,
        };
      });

    // `data.account` is a snapshot, so without this the badge and Re-connect button
    // would not react to a disconnect made from inside this dialog.
    //
    this.watchAccountInStore();

    this.actions$.pipe(
      ofType(smartInboxActions.LoadSignaturesSuccess),
      takeUntil(this.destroy$),
    ).subscribe(({ signatures }) => {
      this.signatures = signatures.length ? signatures : [this.buildDefaultPlaceholder()];
      this.signaturesLoading = false;
      this.syncDefaultIds();
      if (this.selectedSignatureId === null || this.selectedSignatureId === AccountSettingsDialogComponent.DEFAULT_SIGNATURE_PLACEHOLDER_ID) {
        this.selectedSignatureId = this.signatures[0].id;
      }
    });

    this.actions$.pipe(
      ofType(smartInboxActions.LoadSignaturesError),
      takeUntil(this.destroy$),
    ).subscribe(() => {
      this.signaturesLoading = false;
      if (!this.signatures.length) {
        this.signatures = [this.buildDefaultPlaceholder()];
        this.selectedSignatureId = AccountSettingsDialogComponent.DEFAULT_SIGNATURE_PLACEHOLDER_ID;
      }
    });

    this.actions$.pipe(
      ofType(smartInboxActions.CreateSignatureSuccess),
      takeUntil(this.destroy$),
    ).subscribe(({ signature }) => {
      this.signatureSaving = false;
      if (this.selectedSignatureId === AccountSettingsDialogComponent.DEFAULT_SIGNATURE_PLACEHOLDER_ID) {
        this.selectedSignatureId = signature.id;
      }
    });

    this.actions$.pipe(
      ofType(
        smartInboxActions.UpdateSignatureSuccess,
        smartInboxActions.DeleteSignatureSuccess,
      ),
      takeUntil(this.destroy$),
    ).subscribe(() => {
      this.signatureSaving = false;
    });

    this.actions$.pipe(
      ofType(
        smartInboxActions.CreateSignatureError,
        smartInboxActions.UpdateSignatureError,
        smartInboxActions.DeleteSignatureError,
      ),
      takeUntil(this.destroy$),
    ).subscribe(() => {
      this.signatureSaving = false;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Header actions ─────────────────────────────────────────────────────────

  /** Closes with `true` when something changed, so a caller can refresh its list. */
  close(): void {
    this.dialogRef.close(this.mutated);
  }

  openFullSettings(): void {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/settings/smart-inbox'], {
        queryParams: { mailboxId: this.account.id },
      }),
    );
    window.open(url, '_blank');
  }

  // ── Details tab actions ────────────────────────────────────────────────────

  startEditingName(): void {
    this.isEditingName = true;
    this.editedName = this.account.name;
  }

  confirmNameChange(): void {
    const name = this.editedName.trim();
    if (!name) return;
    this.isEditingName = false;
    this.store.dispatch(smartInboxActions.UpdateAccount({
      account: this.account,
      payload: { name },
    }));
  }

  onTypeChange(event: any): void {
    const mailboxType = event.value as MailboxType;
    if (mailboxType === this.account.mailbox_type) return;

    const previousType = this.account.mailbox_type;

    if (!this.canChangeType) {
      this.selectedAccountType = previousType;
      return;
    }

    this.mutated = true;

    if (InboxConfirmationDialogComponent.shouldSkipConfirmation(InboxConfirmationSkipType.ACCOUNT_UPDATE)) {
      this.store.dispatch(smartInboxActions.UpdateAccount({
        account: this.account,
        payload: { mailbox_type: mailboxType },
      }));
      return;
    }

    const actionType = mailboxType === MailboxType.TEAM
      ? InboxConfirmationActionType.SHARE_ACCOUNT
      : InboxConfirmationActionType.UNSHARE_ACCOUNT;

    const dialogRef = this.matDialog.open(InboxConfirmationDialogComponent, {
      data: { actionType, inboxId: this.account.id },
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (result) {
        this.store.dispatch(smartInboxActions.UpdateAccount({
          account: this.account,
          payload: { mailbox_type: mailboxType },
        }));
      } else {
        this.selectedAccountType = previousType;
      }
    });
  }

  onRefresh(): void {
    this.mutated = true;
    this.store.dispatch(smartInboxActions.RefreshAccounts({ accountsIds: [this.account.id] }));
  }

  /**
   * Asks whether to disconnect (reversible) or delete (permanent).
   *
   * Switch on the exact literal: both choices are truthy strings, so `if (choice)` would
   * silently take the destructive branch — the bug that made "Disconnect" wipe everything.
   */
  onDisconnect(): void {
    const dialogRef = this.matDialog.open(InboxDisconnectDialogComponent, {
      panelClass: 'inbox-disconnect-dialog-panel',
      data: {
        email: this.account.email,
        canDisconnect: !this.isDisconnected,
        canDelete: true,
      },
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((choice: InboxDisconnectChoice | undefined) => {
      switch (choice) {
        case 'disconnect_only':
          this.mutated = true;
          this.store.dispatch(smartInboxActions.DisconnectAccount({ account: this.account }));
          // Stay open on Details so the new badge, the explanatory panel and Re-connect
          // are visible, instead of the dialog vanishing as if something was deleted.
          this.activeTabIndex = AccountSettingsTab.Details;
          break;
        case 'delete_connection':
          this.mutated = true;
          this.store.dispatch(smartInboxActions.DeleteAccount({ account: this.account }));
          this.dialogRef.close(true);
          break;
        default:
          break;
      }
    });
  }

  /** Re-runs the hosted-auth flow; the effect owns the popup and its result. */
  onReconnect(): void {
    if (!this.canReconnect) return;
    this.mutated = true;
    this.store.dispatch(smartInboxActions.ReconnectAccount({ account: this.account }));
  }


  // ── Signature tab actions ──────────────────────────────────────────────────

  get selectedSignature(): ISignature | undefined {
    return this.signatures.find((s) => s.id === this.selectedSignatureId);
  }

  isDefaultSignature(sig: ISignature): boolean {
    return this.signatures.indexOf(sig) === 0;
  }

  get selectedSignatureHtml(): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(this.selectedSignature?.body || '');
  }

  get signaturePreviewPlaceholder(): boolean {
    return !this.selectedSignature?.body;
  }

  selectSignature(id: number): void {
    this.selectedSignatureId = id;
    this.renamingSignatureId = null;
  }

  addSignature(): void {
    this.signatureSaving = true;
    const name = `Signature ${this.signatures.length + 1}`;
    this.store.dispatch(smartInboxActions.CreateSignature({
      mailboxId: this.account.id,
      payload: { name, body: '' },
    }));
  }

  removeSignature(sig: ISignature): void {
    const dialogRef = this.matDialog.open(InboxConfirmationDialogComponent, {
      data: { actionType: InboxConfirmationActionType.DELETE_SIGNATURE, inboxId: this.account.id },
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((confirmed) => {
      if (!confirmed) return;
      this.signatureSaving = true;
      if (this.selectedSignatureId === sig.id) {
        this.selectedSignatureId = this.signatures.find((s) => s.id !== sig.id)?.id ?? null;
      }
      this.store.dispatch(smartInboxActions.DeleteSignature({
        mailboxId: this.account.id,
        signatureId: sig.id,
      }));
    });
  }

  startRenamingSignature(sig: ISignature): void {
    this.renamingSignatureId = sig.id;
    this.editedSignatureName = sig.name;
  }

  confirmSignatureRename(): void {
    if (!this.renamingSignatureId || !this.editedSignatureName.trim()) return;
    this.signatureSaving = true;
    this.store.dispatch(smartInboxActions.UpdateSignature({
      mailboxId: this.account.id,
      signatureId: this.renamingSignatureId,
      payload: { name: this.editedSignatureName.trim() },
    }));
    this.renamingSignatureId = null;
  }

  saveSignatureBody(): void {
    const sig = this.selectedSignature;
    if (!sig || !sig.body) return;
    this.signatureSaving = true;

    if (sig.id === AccountSettingsDialogComponent.DEFAULT_SIGNATURE_PLACEHOLDER_ID) {
      this.store.dispatch(smartInboxActions.CreateSignature({
        mailboxId: this.account.id,
        payload: { name: sig.name, body: sig.body },
      }));
    } else {
      this.store.dispatch(smartInboxActions.UpdateSignature({
        mailboxId: this.account.id,
        signatureId: sig.id,
        payload: { body: sig.body },
      }));
    }
  }

  updateSignatureContent(body: string): void {
    this.signatures = this.signatures.map((s) =>
      s.id === this.selectedSignatureId ? { ...s, body } : s,
    );
  }

  setSignatureEditMode(mode: 'html' | 'upload'): void {
    this.signatureEditMode = mode;
  }

  onSignatureFileUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => this.updateSignatureContent(reader.result as string);
    reader.readAsText(file);
  }

  onDefaultNewChange(signatureId: number | null): void {
    this.defaultNewId = signatureId;
    if (signatureId) {
      this.store.dispatch(smartInboxActions.UpdateSignature({
        mailboxId: this.account.id,
        signatureId,
        payload: { is_default_new: true },
      }));
    }
  }

  onDefaultReplyForwardChange(signatureId: number | null): void {
    this.defaultReplyForwardId = signatureId;
    if (signatureId) {
      this.store.dispatch(smartInboxActions.UpdateSignature({
        mailboxId: this.account.id,
        signatureId,
        payload: { is_default_reply_forward: true },
      }));
    }
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  private buildDefaultPlaceholder(): ISignature {
    return {
      id: AccountSettingsDialogComponent.DEFAULT_SIGNATURE_PLACEHOLDER_ID,
      name: 'Default',
      body: '',
      mailbox_id: this.account.id,
      is_default_new: false,
      is_default_reply_forward: false,
      active: true,
      created_at: null,
      updated_at: null,
    };
  }

  private loadSignatures(): void {
    this.signaturesLoading = true;
    this.store.dispatch(smartInboxActions.LoadSignatures({ mailboxId: this.account.id }));
  }

  private syncDefaultIds(): void {
    this.defaultNewId = this.signatures.find((s) => s.is_default_new)?.id ?? null;
    this.defaultReplyForwardId = this.signatures.find((s) => s.is_default_reply_forward)?.id ?? null;
  }

  /** Keeps `account` in step with the store, and closes if it disappears. */
  private watchAccountInStore(): void {
    this.store.select(selectAccounts)
    this.store.select(selectAccounts)
      .pipe(takeUntil(this.destroy$))
      .subscribe((accounts) => {
        const next = accounts.find((a) => a.id === this.account.id);
        if (next) {
          // Server-owned fields only: an in-flight rename must not be clobbered.
          this.account = next;
          return;
        }
        // Deleted, possibly in another tab -- nothing left to configure.
        this.dialogRef.close();
      });
  }

  private resolveOwnerName(): void {
    if (this.data.ownerName?.trim()) {
      this.ownerName = this.data.ownerName.trim();
      return;
    }
    const currentUser = this.authService.getCurrentUser();
    if (currentUser && this.account.mailbox_owner_id === currentUser.id) {
      this.ownerName = currentUser.name || currentUser.type || 'Owner';
    } else {
      this.ownerName = 'Owner';
    }
  }
}
